import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import cn from 'classnames'

import Attachments from 'components/Attachments'
import Draft from 'inbox/draft'
import { SUI, Flex } from 'shared/ui'

import { Dimmer, Loader } from 'util/ui'

import { isEmpty } from 'util/arrays'
import { uninterpolatedTags } from 'util/cannedReply'
import { isCmdEnter } from 'util/keys'
import debug from 'util/debug'
import { runOnNextTick } from 'util/functions'
import storage from 'util/storage'
import withDropzone from 'components/Dropzone'
import { handleDrop } from '../shared/dragAndDrop'

import Footer from './Footer'
import EditorDropzoneIndicator from '../shared/EditorDropzoneIndicator'
import OverflowingDivider from '../shared/OverflowingDivider'
import styles from './styles.css'

const FlexWithDropzone = withDropzone(Flex, {
  indicatorComponent: EditorDropzoneIndicator,
  className: cn(styles.EditorDropzone, 'Dropzone'),
})

const FooterWithDropzone = withDropzone(Footer, {
  skipIndicator: true,
})

class EmailComposer extends React.Component {
  static propTypes = {
    blurred: PropTypes.bool,
    content: PropTypes.string,
    doDeleteReplyDraft: PropTypes.func.isRequired,
    draft: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    editor: PropTypes.func.isRequired,
    focusWhenInitialized: PropTypes.bool,
    header: PropTypes.node,
    hideCollisionIndicator: PropTypes.bool,
    isDraftSendable: PropTypes.bool.isRequired,
    isInsertingCannedReply: PropTypes.bool,
    isForwarding: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool.isRequired, // is ticket open
    onBeforeSend: PropTypes.func,
    onBodyChange: PropTypes.func,
    onEditorBlur: PropTypes.func,
    onEditorFocus: PropTypes.func,
    onEditorResize: PropTypes.func,
    onDeleteDraft: PropTypes.func,
    showAttachmentIcon: PropTypes.bool,
    showToolbarIcon: PropTypes.bool,
    ticketId: PropTypes.string.isRequired,
    updateAndSendDraft: PropTypes.func.isRequired,
    updateDraftDelayed: PropTypes.func.isRequired,
  }

  static defaultProps = {
    blurred: false,
    content: '',
    focusWhenInitialized: false,
    header: undefined,
    hideCollisionIndicator: false,
    isInsertingCannedReply: false,
    onBeforeSend: undefined,
    onBodyChange: undefined,
    onDeleteDraft: undefined,
    onEditorBlur: undefined,
    onEditorFocus: undefined,
    onEditorResize: undefined,
    showAttachmentIcon: false,
    showToolbarIcon: false,
  }

  state = {
    sending: false,
    showToolbar: storage.get('showFormattingToolbar'),
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.ticketId !== this.props.ticketId) this.stopSending()
  }

  componentWillUnmount() {
    this.editorRef = null
    this._unmounting = true
  }

  getContent() {
    return this.editorRef && this.editorRef.getContent()
  }

  setStateAndSend = state => () => {
    return this.updateAndSend({ state })
  }

  preventOnChange = false

  clearContent() {
    if (!this.editorRef) {
      debug('cannot find Editor component to clear')
      return false
    }
    this.editorRef.setContent('')
    return true
  }

  handleOnKeyDownEvent = e => {
    if (isCmdEnter(e)) {
      const { isDraftSendable, prefersOpen } = this.props
      if (isDraftSendable) {
        debug('cmdEnter detected, draft is sendable, sending')
        if (prefersOpen) {
          return this.openAndSend(e)
        }
        return this.closeAndSend(e)
      }
      debug('cmdEnter detected, but draft not sendable, skipping')
      e.preventDefault()
      return false
    }
    return true
  }

  // eslint-disable-next-line no-return-assign
  saveEditorRef = node => (this.editorRef = node)

  startSending = () => this.setState({ sending: true })

  stopSending = () => this.setState({ sending: false })

  openAndSend = this.setStateAndSend('opened')

  closeAndSend = this.setStateAndSend('closed')

  snoozeAndSend = snoozeUntil => {
    return this.updateAndSend({ snoozeUntil, state: 'closed' })
  }

  updateAndSend = changes => {
    const { ticketId, onBeforeSend, updateAndSendDraft } = this.props
    if (!this.checkForUninterpolatedTags()) return false
    if (onBeforeSend && !onBeforeSend(this, this.getContent())) return false

    // if we're sending, we don't care about the draft anymore
    this.clearDraftBufferTimeout()
    this.cancelSendDraftBufferOnBlur()
    this.startSending()

    // Its possible that we have debounced body updates not yet applied.
    // So apply those immediately, then send the draft.
    const currentBody = this.getContent()
    const allChanges = { ...changes }

    // only set body read from ref if it's set
    // there is (most likely) a race condition which causes this to be null
    // if it is the case, we don't want to nullify what's in store
    if (currentBody) {
      allChanges.body = currentBody
    }

    // Since we are now sending the draft, we dont want on/handleChange to try to
    // update the draft in redux or save the draft to the server.
    this.withPreventOnChange(() => this.clearContent())

    return updateAndSendDraft(ticketId, allChanges)
      .then(() => {
        if (this._unmounting) return
        this.stopSending()
      })
      .catch(err => {
        debug(err)
        if (this._unmounting) return
        this.stopSending()

        if (this.editorRef) {
          // Redux store already knows about the restored draft
          this.withPreventOnChange(() => {
            this.editorRef.setContent(allChanges.body)
          })
        }
      })
  }

  withPreventOnChange = fn => {
    const originalPreventOnChange = this.preventOnChange
    this.preventOnChange = true
    const ret = fn()
    this.preventOnChange = originalPreventOnChange
    return ret
  }

  confirmDeleteDraft = () => {
    const { draft } = this.props
    if (Draft.isEmpty(draft)) return true
    const msg = 'Are you sure you want to delete this draft?'
    return window.confirm(msg) // eslint-disable-line no-alert
  }

  handleDeleteDraft = () => {
    if (!this.confirmDeleteDraft()) return false
    this.clearDraftBufferTimeout()
    this.cancelSendDraftBufferOnBlur()

    const { doDeleteReplyDraft, onDeleteDraft, ticketId } = this.props

    this.clearContent()
    doDeleteReplyDraft(ticketId)
    if (this.editorRef) this.editorRef.focus()
    if (onDeleteDraft) onDeleteDraft(ticketId)

    return true
  }

  handleChange = (text, { firstEdit, deletedAll } = {}) => {
    if (this.preventOnChange) return
    const { updateDraftDelayed, ticketId, onBodyChange } = this.props
    this.buffer = text

    if (firstEdit) {
      clearTimeout(this.bufferTimeout)
      updateDraftDelayed(ticketId, 'body', text, { firstEdit, skipSave: true })
    } else if (deletedAll) {
      clearTimeout(this.bufferTimeout)
      this.bufferTimeout = setTimeout(
        this.deleteDraftBuffer(updateDraftDelayed, ticketId, text),
        500
      )
    } else {
      clearTimeout(this.bufferTimeout)
      // Note, its important we bind the current state (i.e. the updateDraftDelayed
      // fn, the ticketId and the draft text) to the debounced function. This
      // ensures the correct draft update will happen, regardless if the ticket
      // props change in the interim (which indeed happens when you navigate to a
      // different ticket page and this component doesnt get unmounted.)
      this.bufferTimeout = setTimeout(
        this.sendDraftBuffer(updateDraftDelayed, ticketId, text),
        3000
      )
    }
    if (onBodyChange) onBodyChange(text)
  }

  sendDraftBuffer = (updateDraftDelayed, ticketId, buffer) => () => {
    this.clearDraftBufferTimeout()
    debug('sendDraftBuffer', { ticketId, buffer })
    updateDraftDelayed(ticketId, 'body', buffer)
  }

  deleteDraftBuffer = (updateDraftDelayed, ticketId, buffer) => () => {
    this.clearDraftBufferTimeout()
    debug('deleteDraftBuffer', { ticketId, buffer })
    updateDraftDelayed(ticketId, 'body', buffer, { deletedAll: true })
  }

  sendContentsOnBlur = () => {
    const { updateDraftDelayed, ticketId, onBlur } = this.props
    const text = this.getContent()

    debug('sendContentsOnBlur: begin', {
      ticketId,
      text,
      bufferTimeout: this.bufferTimeout,
    })

    if (onBlur) onBlur()
    if (!text) return // no draft changes to save

    debug('sendContentsOnBlur: continuing', { ticketId })

    // cancel any pending (typing/blur) updates
    this.clearDraftBufferTimeout()
    this.cancelSendDraftBufferOnBlur()

    // Note, like in `handleChange`, its important we bind the current state (i.e. the updateDraftDelayed
    // fn, the ticketId and the draft text) to the debounced function.
    this.bufferBlurTimeout = setTimeout(
      this.sendDraftBuffer(updateDraftDelayed, ticketId, text),
      500
    )
  }

  cancelSendDraftBufferOnBlur = () => {
    if (this.bufferBlurTimeout) {
      clearTimeout(this.bufferBlurTimeout)
      this.bufferBlurTimeout = null
    }
  }

  clearDraftBufferTimeout = () => {
    if (this.bufferTimeout) {
      clearTimeout(this.bufferTimeout)
      this.bufferTimeout = null
    }
  }

  /* Needed to ensure the editor rerenders when we change tickets */
  editorKey = () => {
    return `reply-editor-ticket-${this.props.draft.ticketId}`
  }

  checkForUninterpolatedTags = () => {
    const { draft } = this.props
    const tags = uninterpolatedTags([draft.title, draft.body])
    if (!isEmpty(tags)) {
      const tagStr = tags.join(', ')
      const msg = `Are you sure you want to send?
Your message contains some tag variables that will automatically be removed if you proceed:
    ${tagStr}
`
      if (!window.confirm(msg)) return false // eslint-disable-line no-alert
    }
    return true
  }

  handleToolbarToggle = () => {
    this.toggleToolbarSemaphore = true
    runOnNextTick(() => {
      // HACK (jscheel): Release valve in case this isn't reset by the blur.
      this.toggleToolbarSemaphore = false
    })
    const { showToolbar } = this.state
    const bookmark = this.editorRef && this.editorRef.selection.getBookmark()
    this.setState({
      showToolbar: !showToolbar,
    })
    storage.set('showFormattingToolbar', !showToolbar)
    if (bookmark) {
      this.editorRef.selection.moveToBookmark(bookmark)
    }
  }

  runIfDefined = (handlerFn, ...args) => {
    if (!handlerFn) return false
    if (typeof handlerFn !== 'function') return false
    return handlerFn(...args)
  }

  handleFocus = evt => {
    // HACK (jscheel): We wait for the next tick so that we can catch the click
    // semaphore from the toolbar click. We need to do this because the click
    // event fires _after_ the blur event does.
    runOnNextTick(() => {
      if (this.toggleToolbarSemaphore) {
        this.runIfDefined(this.props.onEditorFocus, evt)
      }
      this.toggleToolbarSemaphore = false
    })
  }

  handleBlur = evt => {
    // HACK (jscheel): We wait for the next tick so that we can catch the click
    // semaphore from the toolbar click. We need to do this because the click
    // event fires _after_ the blur event does.
    runOnNextTick(() => {
      if (this.toggleToolbarSemaphore) {
        this.sendContentsOnBlur()
        this.runIfDefined(this.props.onEditorBlur, evt)
      }
      this.toggleToolbarSemaphore = false
    })
  }

  render() {
    const {
      blurred,
      className,
      content,
      draft,
      editor: Editor,
      focusWhenInitialized,
      header,
      hideCollisionIndicator,
      isDraftSendable,
      notSendableReason,
      isForwarding,
      isInsertingCannedReply,
      isOpen,
      onBeforeSend,
      onEditorBlur, // dont passthru
      onEditorFocus, // dont passthru
      onEditorResize, // dont passthru
      showAttachmentIcon,
      showToolbarIcon,
      ticketId,
      updateDraftDelayed,
      wrapperClass,
      ...rest
    } = this.props
    const { sending, showToolbar } = this.state
    const dimmed = blurred || isInsertingCannedReply

    return (
      <SUI className={className}>
        <Dimmer.Dimmable dimmed={dimmed} className={wrapperClass}>
          <Flex className={cn('Composer grui flex-col', wrapperClass)}>
            {header}
            <FlexWithDropzone
              className={cn(
                'editorAndAttachments grui flex-col flex-2-1-auto',
                {
                  fwd: isForwarding,
                }
              )}
              onDropCustom={handleDrop}
            >
              <Editor
                contentKey={this.editorKey()}
                className="editor"
                content={content}
                forwardRef={this.saveEditorRef}
                isForwarding={isForwarding}
                focusWhenInitialized={focusWhenInitialized}
                {...rest}
                onBlur={this.handleBlur}
                onFocus={this.handleFocus}
                onResize={onEditorResize}
                onChange={this.handleChange}
                onKeyDown={this.handleOnKeyDownEvent}
              />
              <Attachments forDraft />
            </FlexWithDropzone>
            <OverflowingDivider horizontal />
            <FooterWithDropzone
              actionLabel={ticketId === 'new' ? 'Send' : 'Reply'}
              draftAssignedGroupId={draft.assigned_group_id}
              draftAssignment={draft.assignment}
              editorRef={this.editorRef}
              hideCollisionIndicator={hideCollisionIndicator}
              isDraftSendable={isDraftSendable}
              notSendableReason={notSendableReason}
              isOpen={isOpen}
              onBeforeSend={onBeforeSend}
              onDeleteDraft={this.handleDeleteDraft}
              onSendOpenClick={this.openAndSend}
              onSendClosedClick={this.closeAndSend}
              onSnoozeOptionClick={this.snoozeAndSend}
              onToolbarToggle={this.handleToolbarToggle}
              sending={sending}
              showAttachmentIcon={showAttachmentIcon}
              showToolbar={showToolbar}
              showToolbarIcon={showToolbarIcon}
              ticketId={ticketId}
            />
          </Flex>
          <Dimmer active={dimmed} inverted>
            <Loader size="large" />
          </Dimmer>
        </Dimmer.Dimmable>
      </SUI>
    )
  }
}

export default styled(EmailComposer)`
  .Composer {
    min-height: 106px;
  }

  .editorAndAttachments {
    min-height: ${props => props.placeholderHeight || 50}px;
    margin-bottom: ${props => props.theme.spacing['5']};
  }

  .editorAndAttachments.fwd {
    background-color: ${props => props.theme.color.secondary.positive};
  }
`
